import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from './supabase';
import ImageUploadField from './components/ImageUploadField';
import PDFUploadField from './components/PDFUploadField';
import { uploadToSupabaseStorage } from './utils/uploadUtils';
import { buildAuthRedirectPath } from './utils/authRedirect';

export default function SignUpPage() {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [imageUrl, setImageUrl] = useState('');
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [pdfNotes, setPdfNotes] = useState<{ url: string; name: string; type?: string; description?: string }[]>([]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [mobile, setMobile] = useState('');
  const [subject, setSubject] = useState('');
  const [district, setDistrict] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [medium, setMedium] = useState('');
  const [specificLocation, setSpecificLocation] = useState('');
  const [education, setEducation] = useState('');
  const [university, setUniversity] = useState('');

  // Redirect if already logged in as teacher
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) navigate('/dashboard', { replace: true });
    });
  }, [navigate]);

  const districts = [
    'Colombo', 'Gampaha', 'Kalutara', 'Kandy', 'Matale', 'Nuwara Eliya', 'Galle', 'Matara', 'Hambantota',
    'Jaffna', 'Kilinochchi', 'Mannar', 'Vavuniya', 'Mullaitivu', 'Batticaloa', 'Ampara', 'Trincomalee',
    'Kurunegala', 'Puttalam', 'Anuradhapura', 'Polonnaruwa', 'Badulla', 'Monaragala', 'Ratnapura', 'Kegalle'
  ];

  const sriLankanUniversities = [
    'University of Colombo', 'University of Peradeniya', 'University of Sri Jayewardenepura',
    'University of Moratuwa', 'University of Jaffna', 'Wayamba University, Sri Lanka',
    'Eastern University, Sri Lanka', 'South Eastern University of Sri Lanka',
    'Sabaragamuwa University of Sri Lanka', 'Rajarata University of Sri Lanka',
    'Ruhuna University', 'Open University of Sri Lanka', 'Other'
  ];

  const qualifications = [
    'BSc',
    'BSc (Hons)',
    'BA',
    'BA (Hons)',
    'BCom',
    'BEd',
    'BET',
    'BTech',
    'BEng',
    'BBA',
    'LLB',
    'MBBS',
    'BVSc',
    'HND',
    'HNDE',
    'NDT',
    'NDES',
    'Diploma',
    'Higher Diploma',
    'National Diploma',
    'PGDE',
    'PG Diploma',
    'MSc',
    'MA',
    'MEd',
    'MPhil',
    'PhD',
    'AAT',
    'CIMA',
    'ACCA',
    'CA Sri Lanka',
    'Other'
  ];

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');
    localStorage.setItem('pendingOAuthIntent', 'google-auth');
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: buildAuthRedirectPath('/google-signup') },
    });
    if (error) { setError('Google Sign-In failed: ' + error.message); setLoading(false); }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      setLoading(false);
      return;
    }

    const createTeacherProfile = async (userId: string, accountEmail: string) => {
      const { data: existingTeacher, error: existingTeacherError } = await supabase
        .from('teachers')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle();

      if (existingTeacherError) throw existingTeacherError;
      if (existingTeacher) {
        return { alreadyExists: true };
      }

      let uploadedImageUrl = imageUrl || '';

      if (selectedImageFile) {
        try {
          uploadedImageUrl = await uploadToSupabaseStorage(selectedImageFile, 'teacher-images', userId);
        } catch (uploadError: any) {
          console.error('Signup image upload error:', uploadError);
          uploadedImageUrl = '';
        }
      }

      const { error: insertError } = await supabase.from('teachers').insert({
        user_id: userId,
        name: fullName,
        email: accountEmail,
        mobile,
        whatsapp,
        subject,
        district,
        specific_location: specificLocation,
        medium,
        bio: `${subject} teacher in ${district}${specificLocation ? ', ' + specificLocation : ''}. Teaching in ${medium} medium.`,
        education: education || null,
        university: university || null,
        image_url: uploadedImageUrl || null,
        pdf_urls: pdfNotes.length > 0 ? pdfNotes : [],
      });

      if (insertError) throw insertError;
      return { alreadyExists: false };
    };

    try {
      let userId = '';
      let accountEmail = email;

      const isGmail = email.toLowerCase().endsWith('@gmail.com');

      // 1. Create auth user or recover an existing auth account with the supplied password.
      const { data: authData, error: signUpError } = await supabase.auth.signUp({ email, password });

      if (signUpError) {
        const msg = signUpError.message || '';
        const isAlreadyRegistered =
          msg.includes('already registered') ||
          msg.includes('already been registered') ||
          msg.includes('User already registered');

        if (isAlreadyRegistered) {
          // If it's a Gmail, the account likely exists via Google OAuth — no password set.
          if (isGmail) {
            throw new Error(
              'This Gmail address is already registered via Google. Please use the "Sign up with Google" button instead.'
            );
          }

          // Attempt password sign-in to recover the existing account.
          const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({ email, password });

          if (signInError || !signInData.user) {
            throw new Error(
              'An account with this email already exists. Please log in with your existing password, or use "Sign up with Google" if you registered with Google.'
            );
          }

          userId = signInData.user.id;
          accountEmail = signInData.user.email || email;
        } else {
          throw signUpError;
        }
      } else {
        if (!authData.user) throw new Error('User creation failed.');

        // Supabase returns identities:[] (no error) when email confirmation is ON
        // and the email is already registered — detect this "fake success" case.
        if (authData.user.identities && authData.user.identities.length === 0) {
          if (isGmail) {
            throw new Error(
              'This Gmail address is already registered via Google. Please use the "Sign up with Google" button instead.'
            );
          } else {
            throw new Error(
              'An account with this email already exists. Please log in with your existing password.'
            );
          }
        }

        userId = authData.user.id;
        accountEmail = authData.user.email || email;
      }

      // Block signup if this email is already registered as a student
      const { data: existingStudent } = await supabase
        .from('students')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle();

      if (existingStudent) {
        await supabase.auth.signOut();
        throw new Error(
          'This email is already registered as a Student account. One email can only be used for one role — Teacher or Student — not both.'
        );
      }

      const { alreadyExists } = await createTeacherProfile(userId, accountEmail);

      localStorage.setItem('userRole', 'teacher');
      if (alreadyExists) {
        navigate('/dashboard');
        return;
      }

      navigate('/dashboard');
    } catch (error: any) {
      const msg: string = error.message || '';
      const isGmail = email.toLowerCase().endsWith('@gmail.com');
      let errorMessage: string;

      if (
        msg.includes('already registered') ||
        msg.includes('already been registered') ||
        msg.includes('User already registered')
      ) {
        errorMessage = isGmail
          ? 'This Gmail address is already registered via Google. Please use the "Sign up with Google" button instead.'
          : 'An account with this email already exists. Please log in with your existing password.';
      } else if (msg.includes('Invalid login credentials') || msg.includes('invalid_credentials')) {
        errorMessage = isGmail
          ? 'This Gmail address is registered via Google Sign-In and does not have a password. Please use the "Sign up with Google" button.'
          : 'Incorrect password for this email. Please try logging in instead.';
      } else {
        errorMessage = msg || 'An unexpected error occurred. Please try again.';
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page-shell">
      <div className="page-panel auth-card auth-card-wide" style={{ boxShadow: '0 20px 55px rgba(15,23,42,0.08)', border: '1px solid rgba(148,163,184,0.16)' }}>
        <div className="auth-header">
          <div className="auth-icon-badge" style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>E</div>
          <p className="hero-eyebrow" style={{ display: 'inline-block' }}>Join LankaTutor</p>
          <h1 className="page-title" style={{ textAlign: 'center', marginBottom: '0.75rem' }}>Teacher Registration</h1>
          <p className="auth-subcopy">Create a teacher profile that looks professional on every device and helps students understand your strengths quickly.</p>
        </div>

        <form className="auth-form" onSubmit={handleSubmit}>
          {error && <div className="auth-feedback error">{error}</div>}

          <div className="auth-section-card">
            <div className="auth-section-header">
              <h3 className="auth-section-title">Basic profile</h3>
              <p className="auth-section-copy">Start with the core details students will notice first.</p>
            </div>
            <div className="auth-form-grid">
              <input type="text" id="teacher-signup-full-name" name="fullName" className="input-field" placeholder="Full Name" value={fullName} onChange={(e) => setFullName(e.target.value)} required />
              <input type="tel" id="teacher-signup-mobile" name="mobile" className="input-field" placeholder="Mobile Number" value={mobile} onChange={(e) => setMobile(e.target.value)} required />
              <input type="text" id="teacher-signup-subject" name="subject" className="input-field" placeholder="Subject (e.g. Mathematics)" value={subject} onChange={(e) => setSubject(e.target.value)} required />
              <input type="tel" id="teacher-signup-whatsapp" name="whatsapp" className="input-field" placeholder="WhatsApp Number (e.g. 0771234567)" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} required />
            </div>
          </div>

          <div className="auth-section-card">
            <div className="auth-section-header">
              <h3 className="auth-section-title">Teaching details</h3>
              <p className="auth-section-copy">Help students understand where you teach and how you teach.</p>
            </div>
            <div className="auth-form-grid">
              <div className="select-wrapper">
                <select id="teacher-signup-district" name="district" className="input-field" value={district} onChange={(e) => setDistrict(e.target.value)} required style={{ width: '100%', appearance: 'none', cursor: 'pointer' }}>
                  <option value="" disabled>Select District</option>
                  {districts.map(d => <option key={d} value={d} style={{ color: '#000' }}>{d}</option>)}
                </select>
              </div>
              <input type="text" id="teacher-signup-specific-location" name="specificLocation" className="input-field" placeholder="Specific Location (e.g. Nugegoda)" value={specificLocation} onChange={(e) => setSpecificLocation(e.target.value)} />
              <div className="select-wrapper">
                <select id="teacher-signup-medium" name="medium" className="input-field" value={medium} onChange={(e) => setMedium(e.target.value)} required style={{ width: '100%', appearance: 'none', cursor: 'pointer' }}>
                  <option value="" disabled>Select Teaching Medium</option>
                  <option value="English">English</option>
                  <option value="Tamil">Tamil</option>
                  <option value="Sinhala">Sinhala</option>
                </select>
              </div>
            </div>
          </div>

          <div className="auth-section-card">
            <div className="auth-section-header">
              <h3 className="auth-section-title">Academic background</h3>
              <p className="auth-section-copy">Give your profile more credibility with your qualification and university.</p>
            </div>
            <div className="auth-form-grid">
              <div className="select-wrapper">
                <select id="teacher-signup-education" name="education" className="input-field" value={education} onChange={(e) => setEducation(e.target.value)} style={{ width: '100%', appearance: 'none', cursor: 'pointer' }}>
                  <option value="">Select Qualification</option>
                  {qualifications.map((qualification) => <option key={qualification} value={qualification} style={{ color: '#000' }}>{qualification}</option>)}
                </select>
              </div>
              <div className="select-wrapper">
                <select id="teacher-signup-university" name="university" className="input-field" value={university} onChange={(e) => setUniversity(e.target.value)} style={{ width: '100%', appearance: 'none', cursor: 'pointer' }}>
                  <option value="" disabled>Select Your University</option>
                  {sriLankanUniversities.map(uni => <option key={uni} value={uni} style={{ color: '#000' }}>{uni}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div className="auth-section-card">
            <div className="auth-section-header">
              <h3 className="auth-section-title">Profile photo</h3>
              <p className="auth-section-copy">Optional, but recommended. Choose your photo now and we will upload it securely right after your account is created.</p>
            </div>
            <div style={{ padding: '0.25rem 0' }}>
              <ImageUploadField
                userId="pending"
                onImageUploaded={setImageUrl}
                onError={setError}
                allowPendingSelection
                onPendingFileSelected={setSelectedImageFile}
              />
            </div>
          </div>

          <div className="auth-section-card">
            <div className="auth-section-header">
              <h3 className="auth-section-title">Study materials</h3>
              <p className="auth-section-copy">Optional notes or PDFs can make your listing feel more complete. Upload them after signup from your authenticated dashboard.</p>
            </div>
            <div style={{ padding: '0.25rem 0' }}>
              <PDFUploadField userId="pending" currentPDFs={pdfNotes} onPDFsUpdated={setPdfNotes} onError={setError} />
            </div>
          </div>

          <div className="auth-section-card">
            <div className="auth-section-header">
              <h3 className="auth-section-title">Login details</h3>
              <p className="auth-section-copy">These credentials will be used to access your dashboard after registration.</p>
            </div>
            <div className="auth-form-grid">
              <input type="email" id="teacher-signup-email" name="email" className="input-field" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} required />
              <input type="password" id="teacher-signup-password" name="password" className="input-field" placeholder="Create a password (min 8 chars)" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn primary" disabled={loading}>{loading ? 'Creating Account...' : 'Create Account'}</button>
            <button type="button" className="btn secondary" onClick={handleGoogleSignIn} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }} disabled={loading}>
              <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              Sign up with Google
            </button>
            <p className="form-note">Already have an account? <Link to="/login" className="page-link">Login</Link></p>
          </div>
        </form>
      </div>
    </div>
  );
}
